// "Heg spuit program aan": turns the Hybrid lines into a normal MyPack plan.
// Pure logic and the orchestration only. The HTTP calls are passed in as `api` (see HybridPlanAPI.js), so this file can be tested without a server.
//
// Plan product  : one per product (all pack sizes of a product together).
// Dosage        : the bundle dose per spray when the product is in a bundle, otherwise the choice's own dosage.
// Weeks         : round(volume per ha / dose), at least 1, the FIRST weeks of the crop's periods are ticked.
// Order product : created by the server from the plan volume, then overwritten with the exact boxes of the Hybrid screen.

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));
const sizeKey = (v) => String(Number(v));

// One item per ordered product.
export const planItems = (rows, lines, hectares, checks, choices) => {
  const dosePerProduct = new Map();
  (checks || []).forEach((c) => {
    if (c.mainProductId != null && c.mainDose > 0) dosePerProduct.set(c.mainProductId, c.mainDose);
    (c.partners || []).forEach((p) => {
      if (p.productId != null && p.dose > 0) dosePerProduct.set(p.productId, p.dose);
    });
  });

  const byProduct = new Map();
  const skipped = [];
  (lines || []).forEach((l) => {
    const row = rows.find((r) => r.id === l.rowId);
    const boxes = Math.round(num(l.boxes));
    if (!row || boxes <= 0) return;
    // The server keys an order product's packs by UNIT size (pricing2.unit_volume, e.g. 5 for a 4 x 5 L box)
    // and counts UNITS: a box of full_pack_units units. Order pricing multiplies key x quantity.
    const size = num(row.unit_volume) || num(row.full_pack_volume);
    const unitsPerBox = num(row.full_pack_units) || 1;
    if (!Number.isInteger(size) || size <= 0) {
      // The server only keeps whole pack sizes (update_product_packages takes an integer key)
      skipped.push({ name: row.product_name, reason: `Pakgrootte ${size} is nie 'n heelgetal nie` });
      return;
    }
    let item = byProduct.get(row.product_id);
    if (!item) {
      item = { productId: row.product_id, name: row.product_name, volume: 0, packs: {} };
      byProduct.set(row.product_id, item);
    }
    item.volume += boxes * (num(row.full_pack_volume) || size * unitsPerBox);
    item.packs[sizeKey(size)] = (item.packs[sizeKey(size)] || 0) + boxes * unitsPerBox;
  });

  const items = [];
  byProduct.forEach((item) => {
    const choice = (choices || []).filter((c) => c.product_id === item.productId).sort((a, b) => a.id - b.id)[0];
    if (!choice) {
      skipped.push({ name: item.name, reason: "Geen teiken (choice) vir hierdie produk" });
      return;
    }
    const perHa = hectares > 0 ? item.volume / hectares : item.volume;
    const dose = dosePerProduct.get(item.productId) || num(choice.dosage);
    const weeks = dose > 0 ? Math.max(1, Math.round(perHa / dose - 1e-9)) : 1;
    items.push({ ...item, choiceId: choice.id, dosage: dose > 0 ? dose : perHa, weeks, perHa });
  });
  return { items, skipped };
};

// The weeks JSON the plan screens store: one object per period of the crop, {desc, val}.
export const weeksJson = (periods, cropId, count) => {
  const list = (periods || []).filter((p) => p.crop_id === cropId).sort((a, b) => a.order_seq - b.order_seq);
  if (!list.length) throw new Error("Geen periodes vir hierdie gewas gevind nie");
  const n = Math.min(count, list.length);
  return { json: JSON.stringify(list.map((p, i) => ({ desc: p.name, val: i < n }))), ticked: n, capped: count > list.length };
};

// The planting the normal "Plan besonderhede" form would create: whole hectares, no row width.
export const plantingBody = (planName, hectares, cropName) => ({
  programName: planName,
  cultivar: "Hybrid Volume",
  crop: cropName || "",
  farmSize: Math.max(1, Math.round(hectares)),
  rowWidth: 0,
  circles: "Hybrid",
});

export const createHybridPlan = async ({ api, cropId, plantingId, planting, planName, items, periods, now = Date.now() }) => {
  // `planting` = {farmerId, body}: create the planting here, so the user never visits the plan details form
  if (planting) plantingId = (await api.createPlanting(planting.farmerId, planting.body)).id;
  const plan = await api.createPlan({
    planting_id: plantingId,
    planName,
    plantingDate: now,
    archived: false,
    orderStatus_id: 1,
    crop_id: cropId,
  });
  const notes = [];
  try {
    for (const it of items) {
      const w = weeksJson(periods, cropId, it.weeks);
      if (w.capped) notes.push(`${it.name}: ${it.weeks} weke nodig, maar die gewas het net ${w.ticked} periodes.`);
      await api.createPlanProduct({
        planProduct: {
          plan_id: plan.id,
          dosage: it.dosage,
          totalVolume: it.volume,
          weeks: w.json,
          choice_id: it.choiceId,
          crop_id: cropId,
        },
        planProductOther: { name: "n/a", new: true },
      });
    }
    // The server worked out its own pack mix from the volume. Replace it with the boxes the user ordered.
    const orderProducts = await api.getOrderProducts(plan.id);
    for (const it of items) {
      const op = orderProducts.find((o) => o.product_id === it.productId);
      if (!op) throw new Error(`Bestelproduk vir ${it.name} nie gevind nie`);
      const current = JSON.parse(op.packages || "{}");
      const known = new Set(Object.keys(current).map(sizeKey));
      // A size the product does not have (products.packagingSizes) would never show on the order screen
      const unknown = Object.keys(it.packs).filter((k) => !known.has(k));
      if (unknown.length) throw new Error(`${it.name}: pakgrootte ${unknown.join(", ")} is nie 'n verpakking van die produk nie (die bediener ken ${[...known].join(", ")})`);
      const keys = new Set([...known, ...Object.keys(it.packs)]);
      for (const key of keys) {
        const want = it.packs[key] || 0;
        if (num(current[key]) !== want) await api.setPacks(op.id, Number(key), want);
      }
    }
  } catch (e) {
    e.planId = plan.id;
    e.planName = planName;
    throw e;
  }
  return { plan, notes };
};
